import React, { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Card, Title, Text, Button, Group, Stack, Code, Alert } from '@mantine/core';
import { IconAlertTriangle, IconRefresh, IconHome } from '@tabler/icons-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// Error boundary class component
class AdminErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Admin Error Boundary caught an error:', error, errorInfo);
    
    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Log to error tracking service (if available)
    if (typeof window !== 'undefined' && window.console) {
      console.group('🚨 Admin Portal Error');
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Component Stack:', errorInfo.componentStack);
      console.groupEnd();
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      return <ErrorFallback error={this.state.error} onReset={this.handleReset} />;
    }

    return this.props.children;
  }
}

// Functional component for error display
const ErrorFallback: React.FC<{
  error: Error | null;
  onReset: () => void;
}> = ({ error, onReset }) => {
  const isDevelopment = process.env.NODE_ENV === 'development';

  const handleGoHome = () => {
    window.location.href = '/admin/dashboard';
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      padding: '20px',
      backgroundColor: 'var(--mantine-color-gray-0)'
    }}>
      <Card 
        shadow="md" 
        padding="xl" 
        radius="md" 
        withBorder
        style={{ maxWidth: 600, width: '100%' }}
      >
        <Stack gap="lg">
          <Group>
            <IconAlertTriangle size={48} color="var(--mantine-color-red-6)" />
            <div>
              <Title order={2}>Something went wrong</Title>
              <Text size="sm" c="dimmed">
                An unexpected error occurred in the admin portal
              </Text>
            </div>
          </Group>

          <Alert 
            icon={<IconAlertTriangle size={16} />} 
            title="Error Details" 
            color="red" 
            variant="light"
          >
            <Text size="sm" fw={500}>
              {error?.message || 'An unknown error occurred'}
            </Text>
          </Alert>

          {isDevelopment && error?.stack && (
            <details style={{ cursor: 'pointer' }}>
              <summary>
                <Text size="sm" c="dimmed">
                  Show technical details (development only)
                </Text>
              </summary>
              <Code block mt="sm" style={{ fontSize: '12px' }}>
                {error.stack}
              </Code>
            </details>
          )}

          <Group justify="center" mt="md">
            <Button
              variant="filled"
              leftSection={<IconRefresh size={16} />}
              onClick={onReset}
            >
              Try Again
            </Button>
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              onClick={handleRefresh}
            >
              Refresh Page
            </Button>
            <Button
              variant="subtle"
              leftSection={<IconHome size={16} />}
              onClick={handleGoHome}
            >
              Go to Dashboard
            </Button>
          </Group>

          <Text size="xs" c="dimmed" ta="center">
            If this problem persists, please contact your system administrator
          </Text>
        </Stack>
      </Card>
    </div>
  );
};

// Hook to use with functional components
export const useAdminErrorHandler = () => {
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  const resetError = () => setError(null);
  const throwError = (error: Error) => setError(error);

  return { throwError, resetError };
};

// Export the wrapper component
export const AdminErrorBoundary: React.FC<Props> = (props) => {
  return <AdminErrorBoundaryClass {...props} />;
};

export default AdminErrorBoundary;